import { useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { useApi } from "../../lib/useApi";
import { api, formatApiError } from "../../lib/api";
import { usePlatformCan } from "../../lib/platform";
import { Loader, ErrorState } from "../common/States";
import ReasonDialog from "../admin/ReasonDialog";
import { fmtDate } from "../../lib/labels";
import { METHOD, money } from "../../lib/billingLabels";

const Target = ({ m, data, onReconcile }) => {
  const [v, setV] = useState(m.suggestions[0] ? `p:${m.suggestions[0]}` : "");
  const payments = data.payments.filter((p) => p.currency === m.currency);
  const invoices = data.invoices.filter((i) => i.currency === m.currency && i.balance >= m.amount - 0.005);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <select className="field-select max-w-md" value={v} onChange={(e) => setV(e.target.value)} data-testid={`rec-target-${m.number}`}>
        <option value="">Choisir une transaction confirmée ou une facture</option>
        <optgroup label="Transactions confirmées">{payments.map((p) => <option key={p.id} value={`p:${p.id}`}>{m.suggestions.includes(p.id) ? "★ " : ""}{p.invoice_number} · {money(p.amount, p.currency)} · {METHOD[p.method]} · réf. {p.reference}</option>)}</optgroup>
        <optgroup label="Factures ouvertes (enregistre le paiement)">{invoices.map((i) => <option key={i.id} value={`i:${i.id}`}>{i.number} · {i.company_name} · solde {money(i.balance, i.currency)}</option>)}</optgroup>
      </select>
      <Button size="sm" disabled={!v} onClick={() => onReconcile(m, v)} data-testid={`rec-submit-${m.number}`}>Rapprocher</Button>
    </div>
  );
};

export default function ReconciliationTab() {
  const can = usePlatformCan();
  const { data, loading, error, reload } = useApi("/admin/treasury/reconciliation");
  const done = useApi("/admin/treasury/movements?status=validated&direction=in");
  const [undo, setUndo] = useState(null);
  const run = async (fn, msg) => { try { await fn(); toast.success(msg); setUndo(null); reload(); done.reload(); } catch (e) { toast.error(formatApiError(e)); } };
  const reconcile = (m, v) => run(() => api.post(`/admin/treasury/movements/${m.id}/reconcile`, v.startsWith("p:") ? { payment_id: v.slice(2) } : { invoice_id: v.slice(2) }), "Rapprochement enregistré");
  if (loading) return <Loader />;
  if (error) return <ErrorState message={error} onRetry={reload} />;
  const reconciled = (done.data?.items || []).filter((m) => m.reconciliation.status === "reconciled");
  return (
    <div className="space-y-6">
      <p className="text-sm text-slate-500">Rapprochez chaque encaissement validé avec une transaction confirmée (même montant et même devise) ou une facture ouverte. Les transactions confirmées ne sont jamais modifiées ; annuler un rapprochement est motivé et journalisé.</p>
      <div className="card-surface divide-y" data-testid="rec-pending">
        <p className="p-4 font-display font-semibold">Encaissements à rapprocher ({data.movements.length})</p>
        {!data.movements.length && <p className="p-4 text-sm text-slate-500">Aucun encaissement en attente.</p>}
        {data.movements.map((m) => (
          <div key={m.id} className="space-y-2 p-4" data-testid={`rec-row-${m.number}`}>
            <p className="text-sm"><b>{m.number}</b> · {m.date} · {m.label} · <b>{money(m.amount, m.currency)}</b> · réf. {m.reference}</p>
            {can("treasury:validate") && <Target m={m} data={data} onReconcile={reconcile} />}
          </div>))}
      </div>
      <div className="card-surface" data-testid="rec-unlinked-payments">
        <p className="p-4 font-display font-semibold">Transactions confirmées sans encaissement rapproché ({data.payments.length})</p>
        <ul className="divide-y text-sm">{data.payments.map((p) => <li key={p.id} className="px-4 py-2">{fmtDate(p.paid_at, false)} · {p.invoice_number} · {p.company_name} · {money(p.amount, p.currency)} · réf. {p.reference}</li>)}</ul>
      </div>
      <div className="card-surface" data-testid="rec-done">
        <p className="p-4 font-display font-semibold">Rapprochés récemment</p>
        <ul className="divide-y text-sm">{reconciled.map((m) => <li key={m.id} className="flex flex-wrap items-center justify-between gap-2 px-4 py-2">
          <span>{m.number} · {money(m.amount, m.currency)} ↔ {m.reconciliation.invoice_number} ({m.reconciliation.by})</span>
          {can("treasury:validate") && <Button size="sm" variant="ghost" onClick={() => setUndo(m)} data-testid={`rec-undo-${m.number}`}>Annuler le rapprochement</Button>}</li>)}</ul>
      </div>
      {undo && <ReasonDialog testId="rec-undo" title={`Annuler le rapprochement de ${undo.number}`} label="Confirmer" placeholder="Motif (journalisé)"
        onConfirm={(reason) => run(() => api.post(`/admin/treasury/movements/${undo.id}/unreconcile`, { reason }), "Rapprochement annulé")} onClose={() => setUndo(null)} />}
    </div>
  );
}
